import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSettings, FiKey, FiCpu, FiGlobe, FiEdit, FiSave, FiTrash2 } = FiIcons;

const ApiSettings = ({ apiKey, setApiKey, modelName, setModelName, apiUrl, setApiUrl, apiProvider, setApiProvider }) => {
  const [customModel, setCustomModel] = useState('');
  const [selectedModel, setSelectedModel] = useState(modelName);
  const [showApiKeyField, setShowApiKeyField] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [straicoModels, setStraicoModels] = useState([
    'anthropic/claude-3.7-sonnet:thinking',
    'meta-llama/llama-4-maverick',
    'anthropic/claude-3.5-sonnet',
    'anthropic/claude-3.5-haiku'
  ]);
  const [straicoCustomModel, setStraicoCustomModel] = useState('');
  const [providerApiKeys, setProviderApiKeys] = useState({
    openrouter: '',
    openai: '',
    straico: '',
    custom: ''
  });

  // Load saved settings on component mount
  useEffect(() => {
    const savedApiKey = localStorage.getItem('blocksmith_api_key');
    const savedModelName = localStorage.getItem('blocksmith_model_name');
    const savedApiUrl = localStorage.getItem('blocksmith_api_url');
    const savedApiProvider = localStorage.getItem('blocksmith_api_provider');
    const savedStraicoModels = localStorage.getItem('blocksmith_straico_models');

    // Load provider-specific API keys
    const savedOpenrouterKey = localStorage.getItem('blocksmith_openrouter_key');
    const savedOpenaiKey = localStorage.getItem('blocksmith_openai_key');
    const savedStraicoKey = localStorage.getItem('blocksmith_straico_key');
    const savedCustomKey = localStorage.getItem('blocksmith_custom_key');

    if (savedApiKey) setApiKey(savedApiKey);
    if (savedModelName) setModelName(savedModelName);
    if (savedApiUrl) setApiUrl(savedApiUrl);
    if (savedApiProvider) setApiProvider(savedApiProvider);

    // Set provider-specific keys
    setProviderApiKeys({
      openrouter: savedOpenrouterKey || '',
      openai: savedOpenaiKey || '',
      straico: savedStraicoKey || '',
      custom: savedCustomKey || ''
    });

    // If we have the current provider's key, set it as the active key
    if (savedApiProvider && (
      (savedApiProvider === 'openrouter' && savedOpenrouterKey) || 
      (savedApiProvider === 'openai' && savedOpenaiKey) || 
      (savedApiProvider === 'straico' && savedStraicoKey) || 
      (savedApiProvider === 'custom' && savedCustomKey)
    )) {
      const keyToUse = 
        savedApiProvider === 'openrouter' ? savedOpenrouterKey :
        savedApiProvider === 'openai' ? savedOpenaiKey :
        savedApiProvider === 'straico' ? savedStraicoKey :
        savedCustomKey;
      
      setApiKey(keyToUse || '');
    }

    // Load Straico models if saved
    if (savedStraicoModels) {
      try {
        const parsedModels = JSON.parse(savedStraicoModels);
        if (Array.isArray(parsedModels) && parsedModels.length > 0) {
          setStraicoModels(parsedModels);
        }
      } catch (e) {
        console.error('Error parsing saved Straico models:', e);
      }
    }
  }, []);

  useEffect(() => {
    // Update custom model field if model doesn't match predefined options
    const models = getModelsForProvider(apiProvider);
    if (!models.includes(modelName) && modelName) {
      setCustomModel(modelName);
      setSelectedModel('custom');
    } else {
      setSelectedModel(modelName);
    }
  }, [apiProvider, modelName]);

  const commonModels = [
    'anthropic/claude-3.5-sonnet',
    'openai/gpt-4o',
    'openai/gpt-4o-mini',
    'google/gemini-pro-1.5',
    'meta-llama/llama-3.1-8b-instruct:free'
  ];

  const openaiModels = [
    'gpt-4-turbo',
    'gpt-4o',
    'gpt-4',
    'gpt-3.5-turbo'
  ];

  const getModelsForProvider = (provider) => {
    switch (provider) {
      case 'openrouter': return commonModels;
      case 'openai': return openaiModels;
      case 'straico': return straicoModels;
      default: return [];
    }
  };

  const handleModelChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setSelectedModel('custom');
      setModelName(customModel || '');
    } else {
      setSelectedModel(value);
      setModelName(value);
    }
  };

  const handleCustomModelChange = (e) => {
    const value = e.target.value;
    setCustomModel(value);
    if (selectedModel === 'custom') {
      setModelName(value);
    }
  };

  const handleStraicoModelChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setSelectedModel('custom');
    } else {
      setSelectedModel(value);
      setModelName(value);
    }
  };

  const handleStraicoCustomModelChange = (e) => {
    const value = e.target.value;
    setStraicoCustomModel(value);
    if (selectedModel === 'custom') {
      setModelName(value);
    }
  };

  const addStraicoModel = () => {
    if (straicoCustomModel && !straicoModels.includes(straicoCustomModel)) {
      const newModels = [...straicoModels, straicoCustomModel];
      setStraicoModels(newModels);
      setSelectedModel(straicoCustomModel);
      setModelName(straicoCustomModel);
      setStraicoCustomModel('');
    }
  };

  const handleApiProviderChange = (provider) => {
    setApiProvider(provider);

    // Update API URL based on provider
    if (provider === 'openrouter') {
      setApiUrl('https://openrouter.ai/api/v1/chat/completions');
      setShowApiKeyField(true);
      // Set the API key to the saved provider-specific key
      setApiKey(providerApiKeys.openrouter);
    } else if (provider === 'openai') {
      setApiUrl('https://api.openai.com/v1/chat/completions');
      setShowApiKeyField(true);
      // Set the API key to the saved provider-specific key
      setApiKey(providerApiKeys.openai);
    } else if (provider === 'straico') {
      setApiUrl('https://api.straico.com/v1/prompt/completion');
      setShowApiKeyField(true);
      // Set the API key to the saved provider-specific key
      setApiKey(providerApiKeys.straico);
    } else {
      // Custom API URL will be handled separately
      setShowApiKeyField(true); // Show by default but make it optional
      // Set the API key to the saved provider-specific key
      setApiKey(providerApiKeys.custom);
    }
  };

  // Save the API key for the current provider
  const handleApiKeyChange = (e) => {
    const newKey = e.target.value;
    setApiKey(newKey);
    
    // Update the provider-specific key
    const updatedKeys = { ...providerApiKeys };
    updatedKeys[apiProvider] = newKey;
    setProviderApiKeys(updatedKeys);

    // Save to localStorage immediately for the specific provider
    localStorage.setItem(`blocksmith_${apiProvider}_key`, newKey);
  };

  const clearSavedSettings = () => {
    // Clear localStorage
    localStorage.removeItem('blocksmith_api_key');
    localStorage.removeItem('blocksmith_model_name');
    localStorage.removeItem('blocksmith_api_url');
    localStorage.removeItem('blocksmith_api_provider');
    localStorage.removeItem('blocksmith_straico_models');
    localStorage.removeItem('blocksmith_openrouter_key');
    localStorage.removeItem('blocksmith_openai_key');
    localStorage.removeItem('blocksmith_straico_key');
    localStorage.removeItem('blocksmith_custom_key');

    // Reset state
    setApiKey('');
    setModelName('anthropic/claude-3.5-sonnet');
    setApiUrl('https://openrouter.ai/api/v1/chat/completions');
    setApiProvider('openrouter');
    setCustomModel('');
    setSelectedModel('anthropic/claude-3.5-sonnet');
    setProviderApiKeys({
      openrouter: '',
      openai: '',
      straico: '',
      custom: ''
    });
    setStraicoModels([
      'anthropic/claude-3.7-sonnet:thinking',
      'meta-llama/llama-4-maverick',
      'anthropic/claude-3.5-sonnet',
      'anthropic/claude-3.5-haiku'
    ]);
  };

  const saveSettings = () => {
    // Save general settings
    localStorage.setItem('blocksmith_api_key', apiKey);
    localStorage.setItem('blocksmith_model_name', modelName);
    localStorage.setItem('blocksmith_api_url', apiUrl);
    localStorage.setItem('blocksmith_api_provider', apiProvider);
    localStorage.setItem('blocksmith_straico_models', JSON.stringify(straicoModels));
    
    // Save provider-specific API keys
    localStorage.setItem('blocksmith_openrouter_key', providerApiKeys.openrouter);
    localStorage.setItem('blocksmith_openai_key', providerApiKeys.openai);
    localStorage.setItem('blocksmith_straico_key', providerApiKeys.straico);
    localStorage.setItem('blocksmith_custom_key', providerApiKeys.custom);
    
    // Show success message
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.1 }}
      className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <SafeIcon icon={FiSettings} className="text-slate-600 text-xl" />
          <h2 className="text-xl font-semibold text-slate-800">API Configuration</h2>
        </div>
        <div className="flex gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={saveSettings}
            className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
          >
            <SafeIcon icon={FiSave} className="text-sm" />
            Save Settings
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={clearSavedSettings}
            className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
          >
            <SafeIcon icon={FiTrash2} className="text-sm" />
            Clear Saved
          </motion.button>
        </div>
      </div>

      {saveSuccess && (
        <div className="mb-4 p-2 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
          <SafeIcon icon={FiSave} className="text-sm" />
          Settings saved successfully!
        </div>
      )}

      <div className="mb-6">
        <label className="text-sm font-medium text-slate-700 mb-2 block">API Provider</label>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => handleApiProviderChange('openrouter')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              apiProvider === 'openrouter'
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
            }`}
          >
            <SafeIcon icon={FiGlobe} className="text-sm" />
            OpenRouter
          </button>
          <button
            onClick={() => handleApiProviderChange('openai')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              apiProvider === 'openai'
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
            }`}
          >
            <SafeIcon icon={FiGlobe} className="text-sm" />
            OpenAI
          </button>
          <button
            onClick={() => handleApiProviderChange('straico')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              apiProvider === 'straico'
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
            }`}
          >
            <SafeIcon icon={FiGlobe} className="text-sm" />
            Straico
          </button>
          <button
            onClick={() => handleApiProviderChange('custom')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              apiProvider === 'custom'
                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
            }`}
          >
            <SafeIcon icon={FiEdit} className="text-sm" />
            Custom API
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {showApiKeyField && (
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <SafeIcon icon={FiKey} className="text-slate-500" />
              API Key
              {apiProvider === 'custom' && <span className="text-xs text-slate-500">(Optional)</span>}
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={handleApiKeyChange}
              placeholder={`Enter your ${
                apiProvider === 'openrouter'
                  ? 'OpenRouter'
                  : apiProvider === 'openai'
                  ? 'OpenAI'
                  : apiProvider === 'straico'
                  ? 'Straico'
                  : 'API'
              } key`}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
            <p className="text-xs text-slate-500 mt-1">
              {apiProvider === 'openrouter' && (
                <>
                  Get your API key from{' '}
                  <a
                    href="https://openrouter.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    openrouter.ai
                  </a>
                </>
              )}
              {apiProvider === 'openai' && (
                <>
                  Get your API key from{' '}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    platform.openai.com
                  </a>
                </>
              )}
              {apiProvider === 'straico' && (
                <>
                  Get your API key from{' '}
                  <a
                    href="https://straico.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    straico.com
                  </a>
                </>
              )}
              {apiProvider === 'custom' && 'API key may be optional depending on your service'}
            </p>
          </div>
        )}

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
            <SafeIcon icon={FiCpu} className="text-slate-500" />
            Model Name
          </label>
          
          {apiProvider === 'straico' ? (
            <>
              <select
                value={selectedModel}
                onChange={handleStraicoModelChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 mb-2"
              >
                <optgroup label="Straico Models">
                  {straicoModels.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </optgroup>
                <option value="custom">Add Custom Model</option>
              </select>
              
              {selectedModel === 'custom' && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={straicoCustomModel}
                    onChange={handleStraicoCustomModelChange}
                    placeholder="Enter custom model name"
                    className="flex-1 px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  />
                  <button
                    onClick={addStraicoModel}
                    className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <select
                value={selectedModel}
                onChange={handleModelChange}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 mb-2"
              >
                {apiProvider === 'openrouter' && (
                  <>
                    <optgroup label="OpenRouter Models">
                      {commonModels.map((model) => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))}
                    </optgroup>
                  </>
                )}
                
                {apiProvider === 'openai' && (
                  <>
                    <optgroup label="OpenAI Models">
                      {openaiModels.map((model) => (
                        <option key={model} value={model}>
                          {model}
                        </option>
                      ))}
                    </optgroup>
                  </>
                )}
                
                <option value="custom">Custom Model Name</option>
              </select>
              
              {selectedModel === 'custom' && (
                <input
                  type="text"
                  value={customModel}
                  onChange={handleCustomModelChange}
                  placeholder="Enter custom model name"
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
              )}
            </>
          )}
        </div>
      </div>

      {apiProvider === 'custom' && (
        <div className="mt-6">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
            <SafeIcon icon={FiGlobe} className="text-slate-500" />
            Custom API URL
          </label>
          <input
            type="text"
            value={apiUrl}
            onChange={(e) => setApiUrl(e.target.value)}
            placeholder="https://your-api-endpoint.com/v1/chat/completions"
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          />
          <p className="text-xs text-slate-500 mt-1">
            Enter the full URL for your custom API endpoint (must be compatible with OpenAI API format)
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default ApiSettings;